import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Save, 
  RefreshCw, 
  Database, 
  Shield, 
  Bell,
  Mail,
  Globe,
  Lock,
  Trash2,
  AlertCircle,
  Check,
  Users,
  BookOpen,
  Award,
  GraduationCap,
  BarChart3,
  ClipboardCheck
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';

const Settings = () => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [settings, setSettings] = useState({
    siteName: 'CFA Est Loiret - FACTORY Learning',
    siteDescription: 'Centre de formation d\'apprentis - Formation professionnelle en alternance',
    contactEmail: 'contact@cfa-montargis.com',
    enableNotifications: true,
    enableEmailAlerts: false,
    maintenanceMode: false,
    maxUploadSize: 10,
    sessionTimeout: 60,
    enableRegistration: true
  });

  const [stats, setStats] = useState({
    totalUsers: 0,
    totalFormateurs: 0,
    totalApprenants: 0,
    totalCompetences: 0,
    totalFormations: 0,
    totalEvaluations: 0,
    apprenantsActifs: 0,
    formationsEnCours: 0
  });

  useEffect(() => {
    loadSettings();
    loadStats();
  }, []);

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('*')
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setSettings(data.settings);
      }
    } catch (error) {
      console.error('Erreur chargement paramètres:', error);
    }
  };

  const loadStats = async () => {
    try {
      // Statistiques des utilisateurs
      const [
        users,
        formateurs,
        competences,
        formations,
        apprenants,
        apprenantsActifs,
        evaluations,
        formationsEnCours
      ] = await Promise.all([
        // Total utilisateurs
        supabase.from('utilisateurs').select('id', { count: 'exact', head: true }),
        
        // Total formateurs
        supabase
          .from('utilisateurs')
          .select('id', { count: 'exact', head: true })
          .eq('role', 'formateur'),
        
        // Total compétences
        supabase.from('referentiel_competences').select('id', { count: 'exact', head: true }),
        
        // Total formations
        supabase.from('formations').select('id', { count: 'exact', head: true }),
        
        // Total apprenants
        supabase.from('apprenants').select('id', { count: 'exact', head: true }),
        
        // Apprenants actifs
        supabase
          .from('apprenants')
          .select('id', { count: 'exact', head: true })
          .eq('statut', 'actif'),
        
        // Total évaluations
        supabase.from('niveau_acquisition').select('id', { count: 'exact', head: true }),
        
        // Formations en cours
        supabase
          .from('formations')
          .select('id', { count: 'exact', head: true })
          .lte('date_debut', new Date().toISOString())
          .gte('date_fin', new Date().toISOString())
      ]);

      setStats({
        totalUsers: users.count || 0,
        totalFormateurs: formateurs.count || 0,
        totalApprenants: apprenants.count || 0,
        totalCompetences: competences.count || 0,
        totalFormations: formations.count || 0,
        totalEvaluations: evaluations.count || 0,
        apprenantsActifs: apprenantsActifs.count || 0,
        formationsEnCours: formationsEnCours.count || 0
      });
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('app_settings')
        .upsert({ 
          id: 1, 
          settings,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      showMessage('success', 'Paramètres enregistrés avec succès');
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      showMessage('error', 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleClearCache = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir vider le cache ?')) return;

    setLoading(true);
    try {
      // Vider le cache du navigateur
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      
      // Vider le localStorage
      localStorage.clear();
      
      // Vider le sessionStorage
      sessionStorage.clear();
      
      showMessage('success', 'Cache vidé avec succès');
    } catch (error) {
      console.error('Erreur vidage cache:', error);
      showMessage('error', 'Erreur lors du vidage du cache');
    } finally {
      setLoading(false);
    }
  };

  const handleBackupDatabase = async () => {
    setLoading(true);
    try {
      // Export des principales tables
      const tables = [
        'utilisateurs',
        'formations',
        'referentiel_competences',
        'sous_competences',
        'apprenants',
        'formation_competences',
        'niveau_acquisition'
      ];
      
      const backup = {
        version: '1.0',
        date: new Date().toISOString(),
        tables: {}
      };

      for (const table of tables) {
        const { data, error } = await supabase.from(table).select('*');
        if (error) {
          console.warn(`Avertissement pour la table ${table}:`, error);
          backup.tables[table] = { error: error.message, data: [] };
        } else {
          backup.tables[table] = { data, count: data.length };
        }
      }

      // Créer et télécharger le fichier JSON
      const blob = new Blob([JSON.stringify(backup, null, 2)], {
        type: 'application/json'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup_cfa_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);

      showMessage('success', 'Sauvegarde créée avec succès');
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      showMessage('error', 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleExportStats = async () => {
    setLoading(true);
    try {
      // Récupérer les statistiques détaillées depuis la vue
      const { data: statsData, error } = await supabase
        .from('v_stats_competences')
        .select('*');

      if (error) throw error;

      const statsReport = {
        date_export: new Date().toISOString(),
        statistiques_globales: stats,
        statistiques_competences: statsData
      };

      const blob = new Blob([JSON.stringify(statsReport, null, 2)], {
        type: 'application/json'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stats_cfa_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);

      showMessage('success', 'Statistiques exportées avec succès');
    } catch (error) {
      console.error('Erreur export stats:', error);
      showMessage('error', 'Erreur lors de l\'export des statistiques');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-6">
        {/* Messages */}
        {message.text && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`p-4 rounded-lg mb-6 flex items-center gap-3 ${
              message.type === 'success' 
                ? 'bg-green-600/20 border border-green-600' 
                : 'bg-red-600/20 border border-red-600'
            }`}
          >
            {message.type === 'success' ? (
              <Check className="w-5 h-5 text-green-400" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-400" />
            )}
            <span>{message.text}</span>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Statistiques */}
          <div className="lg:col-span-1 space-y-6">
            {/* Statistiques globales */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 border border-slate-700">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Database className="w-5 h-5 text-blue-400" />
                Statistiques Globales
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Utilisateurs
                  </span>
                  <span className="text-2xl font-bold text-blue-400">{stats.totalUsers}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4" />
                    Formateurs
                  </span>
                  <span className="text-2xl font-bold text-purple-400">{stats.totalFormateurs}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Apprenants
                  </span>
                  <span className="text-2xl font-bold text-green-400">{stats.totalApprenants}</span>
                </div>
                <div className="flex justify-between items-center pl-4">
                  <span className="text-gray-400 text-sm">→ Actifs</span>
                  <span className="text-xl font-bold text-green-300">{stats.apprenantsActifs}</span>
                </div>
              </div>
            </div>

            {/* Statistiques pédagogiques */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 border border-slate-700">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-green-400" />
                Pédagogie
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    Formations
                  </span>
                  <span className="text-2xl font-bold text-orange-400">{stats.totalFormations}</span>
                </div>
                <div className="flex justify-between items-center pl-4">
                  <span className="text-gray-400 text-sm">→ En cours</span>
                  <span className="text-xl font-bold text-orange-300">{stats.formationsEnCours}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 flex items-center gap-2">
                    <Award className="w-4 h-4" />
                    Compétences
                  </span>
                  <span className="text-2xl font-bold text-yellow-400">{stats.totalCompetences}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 flex items-center gap-2">
                    <ClipboardCheck className="w-4 h-4" />
                    Évaluations
                  </span>
                  <span className="text-2xl font-bold text-cyan-400">{stats.totalEvaluations}</span>
                </div>
              </div>
            </div>

            {/* Actions rapides */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 border border-slate-700">
              <h2 className="text-xl font-bold mb-4">Actions Rapides</h2>
              <div className="space-y-3">
                <button
                  onClick={handleBackupDatabase}
                  disabled={loading}
                  className="w-full bg-green-600 hover:bg-green-700 px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                >
                  <Database className="w-5 h-5" />
                  Sauvegarder la base
                </button>
                <button
                  onClick={handleExportStats}
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                >
                  <BarChart3 className="w-5 h-5" />
                  Exporter les stats
                </button>
                <button
                  onClick={handleClearCache}
                  disabled={loading}
                  className="w-full bg-orange-600 hover:bg-orange-700 px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                >
                  <RefreshCw className="w-5 h-5" />
                  Vider le cache
                </button>
                <button
                  onClick={loadStats}
                  disabled={loading}
                  className="w-full bg-slate-600 hover:bg-slate-700 px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                >
                  <RefreshCw className="w-5 h-5" />
                  Actualiser les stats
                </button>
              </div>
            </div>
          </div>

          {/* Paramètres */}
          <div className="lg:col-span-2 space-y-6">
            {/* Paramètres généraux */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 border border-slate-700">
              <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-400" />
                Paramètres généraux
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Nom du site
                  </label>
                  <input
                    type="text"
                    value={settings.siteName}
                    onChange={(e) => setSettings({ ...settings, siteName: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={settings.siteDescription}
                    onChange={(e) => setSettings({ ...settings, siteDescription: e.target.value })}
                    rows={3}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email de contact
                  </label>
                  <input
                    type="email"
                    value={settings.contactEmail}
                    onChange={(e) => setSettings({ ...settings, contactEmail: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Notifications */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 border border-slate-700">
              <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                <Bell className="w-5 h-5 text-purple-400" />
                Notifications
              </h2>
              <div className="space-y-4">
                <label className="flex items-center justify-between cursor-pointer">
                  <div className="flex items-center gap-3">
                    <Bell className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="font-medium">Notifications push</p>
                      <p className="text-sm text-gray-400">Activer les notifications dans l'application</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.enableNotifications}
                    onChange={(e) => setSettings({ ...settings, enableNotifications: e.target.checked })}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                </label>
                <label className="flex items-center justify-between cursor-pointer">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="font-medium">Alertes email</p>
                      <p className="text-sm text-gray-400">Recevoir des alertes par email</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.enableEmailAlerts}
                    onChange={(e) => setSettings({ ...settings, enableEmailAlerts: e.target.checked })}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                </label>
              </div>
            </div>

            {/* Sécurité */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 border border-slate-700">
              <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-400" />
                Sécurité
              </h2>
              <div className="space-y-4">
                <label className="flex items-center justify-between cursor-pointer">
                  <div className="flex items-center gap-3">
                    <Lock className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="font-medium">Mode maintenance</p>
                      <p className="text-sm text-gray-400">Désactiver temporairement l'accès au site</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.maintenanceMode}
                    onChange={(e) => setSettings({ ...settings, maintenanceMode: e.target.checked })}
                    className="w-5 h-5 text-red-600 rounded focus:ring-2 focus:ring-red-500"
                  />
                </label>
                <label className="flex items-center justify-between cursor-pointer">
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="font-medium">Inscription ouverte</p>
                      <p className="text-sm text-gray-400">Autoriser les nouvelles inscriptions</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.enableRegistration}
                    onChange={(e) => setSettings({ ...settings, enableRegistration: e.target.checked })}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                </label>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Taille maximale d'upload (Mo)
                  </label>
                  <input
                    type="number"
                    value={settings.maxUploadSize}
                    onChange={(e) => setSettings({ ...settings, maxUploadSize: parseInt(e.target.value) })}
                    min="1"
                    max="100"
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Timeout de session (minutes)
                  </label>
                  <input
                    type="number"
                    value={settings.sessionTimeout}
                    onChange={(e) => setSettings({ ...settings, sessionTimeout: parseInt(e.target.value) })}
                    min="15"
                    max="480"
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Bouton de sauvegarde */}
            <div className="flex justify-end">
              <button
                onClick={handleSaveSettings}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 px-8 py-3 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-5 h-5" />
                {loading ? 'Enregistrement...' : 'Enregistrer les paramètres'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;